import { useState, useCallback, useEffect, useRef } from 'react';
import {
  getAdminDashboard,
  getAdminPeoplePage,
  AdminPeoplePage,
  AdminProject,
  AdminReviewer,
  subscribeToAdminReviewChanges,
} from '../../services/admin';
import { ReviewRequest } from '../../types';

interface AdminStats {
  totalProjects: number;
  totalUsers: number;
}

interface AdminPermissions {
  canViewAllProjects: boolean;
  canViewReviewQueue: boolean;
  canViewApprovedProjects: boolean;
  canViewPeople: boolean;
  canViewReviewers: boolean;
}

export function useAdminViewModel() {
  const isMountedRef = useRef(true);
  const reviewRefreshInFlightRef = useRef(false);
  const reviewRefreshQueuedRef = useRef(false);
  const [busy, setBusy] = useState(true);
  const [stats, setStats] = useState<AdminStats>({ totalProjects: 0, totalUsers: 0 });
  const [permissions, setPermissions] = useState<AdminPermissions>({
    canViewAllProjects: false,
    canViewReviewQueue: false,
    canViewApprovedProjects: false,
    canViewPeople: false,
    canViewReviewers: false,
  });
  const [projects, setProjects] = useState<AdminProject[]>([]);
  const [reviewQueue, setReviewQueue] = useState<ReviewRequest[]>([]);
  const [approvedProjects, setApprovedProjects] = useState<ReviewRequest[]>([]);
  const [completedProjects, setCompletedProjects] = useState<ReviewRequest[]>([]);
  const [reviewers, setReviewers] = useState<AdminReviewer[]>([]);
  const [peoplePages, setPeoplePages] = useState<AdminPeoplePage[]>([]);
  const [peoplePageIndex, setPeoplePageIndex] = useState(0);
  const [peopleLoading, setPeopleLoading] = useState(false);
  const [peopleError, setPeopleError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const people = peoplePages[peoplePageIndex]?.people || [];
  const canLoadPreviousPeoplePage = peoplePageIndex > 0;
  const canLoadNextPeoplePage = Boolean(peoplePages[peoplePageIndex]?.nextCursor);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const initialize = useCallback(async () => {
    setBusy(true);
    setError(null);
    setPeopleError(null);
    setPeopleLoading(false);
    try {
      const data = await getAdminDashboard();
      if (!isMountedRef.current) return;
      setStats(data.stats);
      setPermissions(data.permissions);
      setProjects(data.projects);
      setReviewQueue(data.reviewQueue);
      setApprovedProjects(data.approvedProjects);
      setCompletedProjects(data.completedProjects);
      setReviewers(data.reviewers);
      setPeoplePages([{ people: data.people, nextCursor: data.peopleNextCursor }]);
      setPeoplePageIndex(0);
    } catch (err) {
      if (!isMountedRef.current) return;
      console.error('Failed to load admin data:', err);
      setError('Failed to load admin data');
    } finally {
      if (isMountedRef.current) {
        setBusy(false);
      }
    }
  }, []);

  const refreshReviewLists = useCallback(async () => {
    if (!permissions.canViewReviewQueue && !permissions.canViewApprovedProjects) {
      return;
    }

    if (reviewRefreshInFlightRef.current) {
      reviewRefreshQueuedRef.current = true;
      return;
    }

    reviewRefreshInFlightRef.current = true;

    try {
      do {
        reviewRefreshQueuedRef.current = false;
        const data = await getAdminDashboard();
        if (!isMountedRef.current) return;
        setPermissions(data.permissions);
        setReviewQueue(data.reviewQueue);
        setApprovedProjects(data.approvedProjects);
        setCompletedProjects(data.completedProjects);
      } while (reviewRefreshQueuedRef.current);
    } catch (err) {
      if (!isMountedRef.current) return;
      console.error('Failed to refresh review lists:', err);
    } finally {
      reviewRefreshInFlightRef.current = false;
    }
  }, [permissions.canViewApprovedProjects, permissions.canViewReviewQueue]);

  useEffect(() => {
    if (!permissions.canViewReviewQueue && !permissions.canViewApprovedProjects) {
      return undefined;
    }

    return subscribeToAdminReviewChanges(() => {
      void refreshReviewLists();
    });
  }, [permissions.canViewApprovedProjects, permissions.canViewReviewQueue, refreshReviewLists]);

  const loadNextPeoplePage = useCallback(async () => {
    const cachedPage = peoplePages[peoplePageIndex + 1];
    if (cachedPage) {
      setPeoplePageIndex((currentIndex) => currentIndex + 1);
      return;
    }

    const cursor = peoplePages[peoplePageIndex]?.nextCursor;
    if (!cursor) return;

    setPeopleLoading(true);
    setPeopleError(null);
    try {
      const nextPage = await getAdminPeoplePage(cursor);
      if (!isMountedRef.current) return;
      setPeoplePages((currentPages) => [...currentPages, nextPage]);
      setPeoplePageIndex((currentIndex) => currentIndex + 1);
    } catch (err) {
      if (!isMountedRef.current) return;
      console.error('Failed to load people page:', err);
      setPeopleError('Failed to load more people');
    } finally {
      if (isMountedRef.current) {
        setPeopleLoading(false);
      }
    }
  }, [peoplePageIndex, peoplePages]);

  const loadPreviousPeoplePage = useCallback(() => {
    if (peoplePageIndex === 0) return;
    setPeoplePageIndex((currentIndex) => currentIndex - 1);
    setPeopleError(null);
  }, [peoplePageIndex]);

  return {
    busy,
    stats,
    permissions,
    projects,
    reviewQueue,
    approvedProjects,
    completedProjects,
    reviewers,
    people,
    peoplePageIndex,
    peopleLoading,
    peopleError,
    canLoadPreviousPeoplePage,
    canLoadNextPeoplePage,
    loadNextPeoplePage,
    loadPreviousPeoplePage,
    error,
    initialize,
  };
}
